import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import MobileNavigation from './MobileNavigation';
import SearchModal from './SearchModal';
import NotificationCenter from '../NotificationCenter';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const isActive = (path) => location?.pathname === path;

  const navigationItems = [
    { label: 'Destinations', path: '/homepage', icon: 'MapPin' },
    { label: 'Hidden Gems', path: '/hidden-gems', icon: 'Gem' },
    { label: 'AI Trip Planner', path: '/trip-planner', icon: 'Sparkles' },
    { label: 'My Trips', path: '/user-dashboard', icon: 'Briefcase' },
    { label: 'Bookings', path: '/bookings-dashboard', icon: 'Calendar' },
    { label: 'Modify Booking', path: '/booking-modification', icon: 'Edit' },
    { label: 'Notifications', path: '/notifications-center', icon: 'Bell' },
    { label: 'Admin', path: '/admin-dashboard', icon: 'Settings' },
    { label: 'Partners', path: '/partners', icon: 'Handshake' },
    { label: 'Safety', path: '/safety', icon: 'Shield' },
  ];

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-[1000] transition-all duration-300 ${isScrolled ? 'shadow-xl' : 'shadow-md'} overflow-hidden`}>
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('/assets/images/Screenshot_2026-02-02_at_11.26.33_PM-1770047932841.png')" }}
        />
        {/* Overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#2E8B8B]/90 to-[#1A6B6B]/85 backdrop-blur-[2px]" />
        
        <div className="max-w-[1400px] mx-auto px-3 md:px-5 h-14 flex items-center justify-between relative z-10">
          {/* Logo */}
          <a 
            href="/homepage" 
            className="flex items-center gap-1 no-underline group"
            onClick={(e) => {
              e?.preventDefault();
              handleNavigation('/homepage');
            }}
          >
            <div className="w-8 h-8 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:bg-white/30 transition-all duration-300">
              <Icon name="Palmtree" size={20} color="#FFFFFF" />
            </div>
            <div className="flex flex-col">
              <span className="font-medium text-white/80 tracking-wide text-xs">TARA LET'S</span>
              <span className="font-bold text-white tracking-tight text-xs">Discover Philippines</span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1.5 overflow-x-auto">
            {navigationItems?.map((item) => (
              <a
                key={item?.path}
                href={item?.path}
                className={`px-2.5 py-1.5 rounded-lg text-[12px] font-medium no-underline transition-all duration-300 whitespace-nowrap ${
                  isActive(item?.path) 
                    ? 'bg-white/20 text-white backdrop-blur-sm' :'text-white/90 hover:bg-white/10 hover:text-white'
                }`}
                onClick={(e) => {
                  e?.preventDefault();
                  handleNavigation(item?.path);
                }}
              >
                {item?.label}
              </a>
            ))}
          </nav>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsSearchModalOpen(true)}
              className="hidden md:flex items-center gap-2 px-3.5 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm transition-all duration-300"
              aria-label="Search"
            >
              <Icon name="Search" size={16} />
              <span className="text-[12px] font-medium">Search</span>
            </button>
            
            <NotificationCenter />

            <button
              className="hidden md:flex items-center justify-center w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm transition-all duration-300"
              aria-label="Favorites"
            >
              <Icon name="Heart" size={18} />
            </button>
            
            <Button
              variant="default"
              size="default"
              className="hidden md:flex bg-[#F4A261] hover:bg-[#E89451] text-gray-900 font-semibold border-0 text-[12px] px-3 py-1.5 h-9"
              iconName="Calendar"
              iconPosition="left"
              onClick={() => handleNavigation('/itinerary-builder')}
            >
              Itinerary Builder
            </Button>

            {/* Mobile Menu Toggle */}
            <button
              className="lg:hidden flex items-center justify-center w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm transition-all duration-300"
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label="Open menu"
            >
              <Icon name="Menu" size={20} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <MobileNavigation
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
          navigationItems={navigationItems}
          onNavigate={handleNavigation}
          activeRoute={location?.pathname}
        />
      )}

      {/* Search Modal */}
      {isSearchModalOpen && (
        <SearchModal
          isOpen={isSearchModalOpen}
          onClose={() => setIsSearchModalOpen(false)}
        />
      )}
    </>
  );
};

export default Header;